import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { filter, take, switchMap } from 'rxjs/operators';
import { HeaderService } from './header.service';
import { BaseApiResponse } from '../../models/api/api.model';
import { StorageService } from './storage.service';
import { StorageKeys } from '../../../../enums/storage-keys.enum';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { CurrentUserService } from '../auth/current-user.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private readonly http: HttpClient,
    private readonly headerService: HeaderService,
    private currentUserService: CurrentUserService,
    private storageService: StorageService,
    private router: Router
  ) {}

  get<T = any>(url: string): Observable<BaseApiResponse<T>> {
    return new Observable((observer) => {
      this.http
        .get<BaseApiResponse<T>>(url, {
          headers: this.headerService.getHeaders(),
        })
        .subscribe({
          next: (res) => {
            console.log(res);
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            if (err.status === 401) {
              this.handle401Error(url, 'get', null, observer);
            } else {
              observer.error(err);
            }
          },
        });
    });
  }

  post<T = any>(
    url: string,
    payload: any,
    customHeaders?: HttpHeaders
  ): Observable<BaseApiResponse<T>> {
    return new Observable((observer) => {
      const headers = customHeaders || this.headerService.getHeaders();

      this.http
        .post<BaseApiResponse<T>>(url, payload, {
          headers: headers,
        })
        .subscribe({
          next: (res) => {
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            const isRefreshUrl = url.includes('refreshToken');
            const hasCustomHeaders = customHeaders !== undefined;

            if (err.status === 401 && !isRefreshUrl && !hasCustomHeaders) {
              this.handle401Error(url, 'post', payload, observer);
            } else {
              if (isRefreshUrl) {
                this.logoutAndRedirect();
              }
              observer.error(err);
            }
          },
        });
    });
  }

  postMultipart<T = any>(
    url: string,
    formData: FormData
  ): Observable<BaseApiResponse<T>> {
    return new Observable((observer) => {
      const headers = this.headerService.getHeaders().delete('Content-Type');

      this.http
        .post<BaseApiResponse<T>>(url, formData, {
          headers: headers,
        })
        .subscribe({
          next: (res) => {
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            if (err.status === 401) {
              this.handle401Error(url, 'postMultipart', formData, observer);
            } else {
              observer.error(err);
            }
          },
        });
    });
  }

  getBlob(url: string): Observable<Blob> {
    return new Observable((observer) => {
      this.http
        .get(url, {
          headers: this.headerService.getHeaders(),
          responseType: 'blob',
        })
        .subscribe({
          next: (res) => {
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            if (err.status === 401) {
              this.handle401ErrorBlob(url, observer);
            } else {
              observer.error(err);
            }
          },
        });
    });
  }

  postBlob(url: string, payload: any): Observable<Blob> {
    return new Observable((observer) => {
      this.http
        .post(url, payload, {
          headers: this.headerService.getHeaders(),
          responseType: 'blob',
        })
        .subscribe({
          next: (res) => {
            observer.next(res);
            observer.complete();
          },
          error: (err) => {
            if (err.status === 401) {
              this.handle401ErrorBlob(url, observer);
            } else {
              observer.error(err);
            }
          },
        });
    });
  }

  private handle401ErrorBlob(url: string, observer: any) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshUrl = `${environment.apiLocations.identity}/Auth/refreshToken`;
      const userData = this.storageService.get(StorageKeys.USER);

      if (!userData || !userData.refreshToken) {
        this.logoutAndRedirect();
        return;
      }

      const refreshPayload = {
        refreshToken: userData.refreshToken,
        token: userData.token,
      };

      this.post(
        refreshUrl,
        refreshPayload,
        this.headerService.getHeadersWithoutAuth()
      ).subscribe((refreshRes) => {
        if (!refreshRes.fail && refreshRes.data) {
          this.storageService.set(StorageKeys.USER, refreshRes.data);
          this.currentUserService.SetSession(refreshRes.data);
          this.refreshTokenSubject.next(refreshRes.data.token);
          this.getBlob(url).subscribe((res) => {
            observer.next(res);
            observer.complete();
          });
          this.isRefreshing = false;
        } else {
          this.isRefreshing = false;
          this.logoutAndRedirect();
          observer.error({ fail: true, Errors: ['Session expired'] });
        }
      });
    } else {
      this.refreshTokenSubject
        .pipe(
          filter((token) => token != null),
          take(1)
        )
        .subscribe(() => {
          this.getBlob(url).subscribe((res) => {
            observer.next(res);
            observer.complete();
          });
        });
    }
  }

  private handle401Error(
    url: string,
    method: string,
    payload: any,
    observer: any
  ) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshUrl = `${environment.apiLocations.identity}/Auth/refreshToken`;

      const userData = this.storageService.get(StorageKeys.USER);

      if (!userData || !userData.refreshToken) {
        this.logoutAndRedirect();
        return;
      }

      const refreshPayload = {
        refreshToken: userData.refreshToken,
        token: userData.token,
      };

      this.post(
        refreshUrl,
        refreshPayload,
        this.headerService.getHeadersWithoutAuth()
      ).subscribe((refreshRes) => {
        if (!refreshRes.fail && refreshRes.data) {
          this.storageService.set(StorageKeys.USER, refreshRes.data);
          this.currentUserService.SetSession(refreshRes.data);

          this.refreshTokenSubject.next(refreshRes.data.token);

          this.retryRequest(url, method, payload, observer);

          this.isRefreshing = false;
        } else {
          this.isRefreshing = false;
          this.logoutAndRedirect();
          observer.next({ fail: true, Errors: ['Session expired'] });
          observer.complete();
        }
      });
    } else {
      this.refreshTokenSubject
        .pipe(
          filter((token) => token != null),
          take(1)
        )
        .subscribe(() => {
          this.retryRequest(url, method, payload, observer);
        });
    }
  }

  private retryRequest(
    url: string,
    method: string,
    payload: any,
    observer: any
  ) {
    if (method === 'get') {
      this.get(url).subscribe((res) => {
        observer.next(res);
        observer.complete();
      });
    } else if (method === 'postMultipart') {
      this.postMultipart(url, payload).subscribe((res) => {
        observer.next(res);
        observer.complete();
      });
    } else {
      this.post(url, payload).subscribe((res) => {
        observer.next(res);
        observer.complete();
      });
    }
  }

  private logoutAndRedirect(): void {
    this.storageService.clearAll();
    this.router.navigateByUrl('/auth/login');
  }
}
